// src/pages/ForumsPage.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { forumApi } from "../lib";
import { showError, showSuccess } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import {
  MessageCircle,
  Users,
  Clock,
  Search,
  MessageSquare,
  Pin,
  TrendingUp,
  ChevronRight,
  BookOpen,
  CheckCircle,
  X,
  Grid3x3,
  List,
  Sparkles,
  Zap,
  Shield,
  ClipboardCheck,
  LogOut,
  Loader,
  Compass,
  Star,
  Award,
  Flame,
  Hash,
  Lock,
  Unlock,
  Heart,
  Eye,
  ArrowLeft,
  Calendar,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import SEO from "../components/SEO";

interface Forum {
  _id: string;
  id: string;
  course: string;
  courseTitle: string;
  courseThumbnail?: string;
  title: string;
  description: string;
  category: string;
  topics: number;
  posts: number;
  memberCount: number;
  lastActivity?: string;
  isJoined: boolean;
  isPinned: boolean;
  isHot?: boolean;
  instructor: {
    user: string;
    name: string;
    avatar?: string;
    role: string;
    posts: number;
  };
  rules?: string[];
  recentDiscussions?: DiscussionPreview[];
}

interface DiscussionPreview {
  id: string;
  title: string;
  author: string;
  authorAvatar?: string;
  replies: number;
  lastActivity: string;
  isPinned: boolean;
  isHot: boolean;
}

interface EnrolledCourse {
  _id: string;
  title: string;
  thumbnail?: string;
  instructorName?: string;
  instructorAvatar?: string;
  forum: Forum | null;
  enrollmentStatus: string;
  progress: number;
}

const ForumsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState<
    "my-forums"  | "trending" | "available"
  >("my-forums");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedForum, setSelectedForum] = useState<Forum | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [agreeToRules, setAgreeToRules] = useState(false);
  
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [forums, setForums] = useState<Forum[]>([]);
  const [availableForums, setAvailableForums] = useState<Forum[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchForums();
  }, []);

  const fetchForums = async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated) {
        const [enrolledResponse, availableResponse] = await Promise.all([
          forumApi.getUserEnrolledCoursesWithForums(),
          forumApi.getAvailableForums(),
        ]);
        
        if (enrolledResponse.success) {
          const courses = enrolledResponse.data;
          setEnrolledCourses(courses);
          
          const allForums: Forum[] = [];
          courses.forEach((item: EnrolledCourse) => {
            if (item.forum) {
              allForums.push({
                ...item.forum,
                isJoined: true,
                isPinned: false,
              });
            }
          });
          setForums(allForums);
        }
        
        if (availableResponse.success) {
          setAvailableForums(availableResponse.data || []);
        }
      } else {
        setForums([]);
        setEnrolledCourses([]);
        setAvailableForums([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch forums:", err);
      showError("Failed to load forums");
    } finally {
      setLoading(false);
    }
  };

  const handleEnterForum = (forum: Forum) => {
    if (!forum.isJoined) {
      setSelectedForum(forum);
      setAgreeToRules(false);
      setShowJoinModal(true);
    } else {
      navigate(`/forums/${forum._id || forum.id}`);
    }
  };

  const openJoinModal = (forum: Forum) => {
    setSelectedForum(forum);
    setAgreeToRules(false);
    setShowJoinModal(true);
  };

  const handleJoinForum = async () => {
    if (!agreeToRules || !selectedForum) return;
    
    try {
      setJoining(true);
      await forumApi.joinForum(selectedForum._id || selectedForum.id);
      showSuccess("Successfully joined the forum!");
      
      setForums((prev) =>
        prev.map((f) =>
          f._id === selectedForum._id || f.id === selectedForum.id 
            ? { ...f, isJoined: true } 
            : f,
        ),
      );
      setAvailableForums((prev) =>
        prev.map((f) =>
          f._id === selectedForum._id || f.id === selectedForum.id 
            ? { ...f, isJoined: true } 
            : f,
        ),
      );
      setShowJoinModal(false);
      navigate(`/forums/${selectedForum._id || selectedForum.id}`);
    } catch (err: any) {
      console.error("Failed to join forum:", err);
      showError("Failed to join forum");
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveForum = async (e: React.MouseEvent, forum: Forum) => {
    e.stopPropagation();
    
    try {
      await forumApi.leaveForum(forum._id || forum.id);
      showSuccess("Left the forum");
      
      setForums((prev) =>
        prev.map((f) => 
          f._id === forum._id || f.id === forum.id 
            ? { ...f, isJoined: false } 
            : f,
        ),
      );
      setAvailableForums((prev) =>
        prev.map((f) => 
          f._id === forum._id || f.id === forum.id 
            ? { ...f, isJoined: false } 
            : f,
        ),
      );
    } catch (err: any) {
      console.error("Failed to leave forum:", err);
      showError("Failed to leave forum");
    }
  };

  const trendingForums = [...forums, ...availableForums]
    .sort((a, b) => (b.posts || 0) - (a.posts || 0))
    .slice(0, 3);

  const filteredForums = (() => {
    let source: Forum[] = [];
    if (activeTab === "available") {
      source = availableForums;
    } else if (activeTab === "joined") {
      source = forums.filter(f => f.isJoined);
    } else if (activeTab === "trending") {
      source = trendingForums;
    } else {
      source = forums.filter(f => enrolledCourses.some(c => c.forum && (c.forum._id === f._id || c.forum.id === f.id)) && f.isJoined);
    }
    
    return source.filter(
      (forum) =>
        (forum.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (forum.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (forum.courseTitle || "").toLowerCase().includes(searchQuery.toLowerCase()),
    );
  })();

  const tabs = [
    { id: "my-forums", label: "My Forums", icon: BookOpen, count: forums.filter((f) => f.isJoined && enrolledCourses.some((c) => c.forum && (c.forum._id === f._id || c.forum.id === f.id))).length },
    { id: "joined", label: "Joined", icon: CheckCircle, count: forums.filter((f) => f.isJoined).length },
    { id: "trending", label: "Trending", icon: Flame, count: trendingForums.length },
    { id: "available", label: "Explore", icon: Compass, count: availableForums.length },
  ];

  const stats = [
    { label: "Total Forums", value: forums.length + availableForums.length, icon: MessageCircle, gradient: "from-orange-500 to-orange-600" },
    { label: "Discussions", value: forums.reduce((s, f) => s + (f.topics || 0), 0) + availableForums.reduce((s, f) => s + (f.topics || 0), 0), icon: MessageSquare, gradient: "from-orange-500 to-orange-600" },
    { label: "Total Posts", value: forums.reduce((s, f) => s + (f.posts || 0), 0) + availableForums.reduce((s, f) => s + (f.posts || 0), 0), icon: Users, gradient: "from-orange-500 to-orange-600" },
    { label: "Joined", value: forums.filter((f) => f.isJoined).length, icon: CheckCircle, gradient: "from-orange-500 to-orange-600" },
  ];

  if (loading) {
    return (
      <>
        <SEO
          title="Course Forums - LearnHub Community"
          description="Join course discussions and connect with peers. Ask questions, share knowledge, and collaborate with fellow learners in our community forums."
          keywords="course forums, community, discussions, Q&A, learnhub community, student forums, tech discussions"
        />
        <main className="min-h-screen pt-20 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Loader className="w-10 h-10 mx-auto mb-3 text-orange-500 animate-spin" />
              <p className="text-sm text-gray-500">Loading forums...</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Course Forums - LearnHub Community"
        description="Join course discussions and connect with peers. Ask questions, share knowledge, and collaborate with fellow learners in our community forums."
        keywords="course forums, community, discussions, Q&A, learnhub community, student forums, tech discussions"
      />
      <main className="min-h-screen pt-20 pb-16 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Hero Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 shadow-lg rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Course Forums</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Connect, ask questions, and share knowledge with your peers</p>
                </div>
              </div>

              {/* Search + View Toggle */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search forums..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-2 pr-3 text-sm bg-white border border-gray-200 sm:w-64 pl-9 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
                <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-1.5 rounded-lg transition-all duration-200 ${
                      viewMode === "grid" ? "bg-orange-500 text-white shadow-sm" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-lg transition-all duration-200 ${
                      viewMode === "list" ? "bg-orange-500 text-white shadow-sm" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 mb-8 md:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className="p-4 transition-all bg-white border border-gray-100 shadow-sm rounded-xl hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-[11px] text-gray-500 font-medium">{s.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 p-1 mb-6 overflow-x-auto bg-white border border-gray-100 shadow-sm rounded-xl">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg whitespace-nowrap transition-all duration-200 sm:px-4 sm:py-2.5 sm:text-sm ${
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-md"
                      : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{tab.label}</span>
                  <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded-full ${
                    activeTab === tab.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Forums Grid/List */}
          {filteredForums.length === 0 ? (
            <div className="py-16 text-center bg-white border border-gray-100 shadow-sm rounded-2xl">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-100 to-orange-200">
                <MessageCircle className="w-10 h-10 text-orange-400" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">No forums found</h3>
              <p className="max-w-sm mx-auto text-gray-500">
                {enrolledCourses.length === 0 && isAuthenticated
                  ? "Enroll in courses to access their forums and connect with peers"
                  : "Try adjusting your search or explore available forums"}
              </p>
              {enrolledCourses.length === 0 && isAuthenticated && (
                <div className="mt-6">
                  <button
                    onClick={() => navigate("/courses")}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg"
                  >
                    <BookOpen className="w-4 h-4" />
                    Browse Courses
                  </button>
                </div>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filteredForums.map((forum) => (
                <div
                  key={forum._id || forum.id}
                  onClick={() => handleEnterForum(forum)}
                  className="overflow-hidden transition-all duration-300 bg-white border border-gray-100 cursor-pointer group rounded-2xl hover:shadow-xl hover:-translate-y-1"
                >
                  {/* Course thumbnail */}
                  <div className="relative h-32 overflow-hidden bg-gradient-to-r from-orange-100 to-orange-200">
                    {forum.courseThumbnail ? (
                      <img
                        src={forum.courseThumbnail}
                        alt={forum.courseTitle}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full">
                        <GraduationCap className="w-12 h-12 text-orange-300" />
                      </div>
                    )}
                    {forum.isHot && (
                      <div className="absolute px-2 py-1 bg-orange-500 rounded-lg shadow-md top-3 right-3 text-white text-[10px] font-bold flex items-center gap-1">
                        <Flame className="w-3 h-3" /> Hot
                      </div>
                    )}
                    {forum.isPinned && (
                      <div className="absolute px-2 py-1 rounded-lg shadow-md top-3 left-3 bg-orange-600 text-white text-[10px] font-bold flex items-center gap-1">
                        <Pin className="w-3 h-3" /> Pinned
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="mb-2">
                      <h3 className="text-base font-bold text-gray-900 transition-colors line-clamp-1 group-hover:text-orange-600">
                        {forum.title}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">{forum.courseTitle}</p>
                    </div>

                    <p className="mb-3 text-xs text-gray-600 line-clamp-2">{forum.description}</p>

                    {/* Stats */}
                    <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{forum.topics || 0}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{forum.memberCount || 0}</span>
                      </div>
                      {forum.lastActivity && <span className="text-[10px] text-gray-400">{forum.lastActivity}</span>}
                    </div>

                    {/* Instructor */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center overflow-hidden text-xs font-bold text-white rounded-full w-7 h-7 bg-gradient-to-br from-orange-400 to-orange-600">
                          {(forum.instructor?.name || "I").charAt(0)}
                        </div>
                        <span className="text-xs text-gray-600 truncate max-w-[100px]">{forum.instructor?.name}</span>
                      </div>
                      {forum.isJoined ? (
                        <button onClick={(e) => handleLeaveForum(e, forum)} className="text-xs font-medium text-red-500 transition-colors hover:text-red-600">
                          Leave
                        </button>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium text-orange-600"><Unlock className="w-3 h-3" /> Join</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredForums.map((forum) => (
                <div
                  key={forum._id || forum.id}
                  onClick={() => handleEnterForum(forum)}
                  className="p-4 transition-all duration-300 bg-white border border-gray-100 cursor-pointer rounded-2xl hover:shadow-lg hover:border-orange-200"
                >
                  <div className="flex flex-col gap-4 sm:flex-row">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0 w-full h-20 overflow-hidden sm:w-20 rounded-xl bg-gradient-to-br from-orange-100 to-orange-200">
                      {forum.courseThumbnail ? (
                        <img src={forum.courseThumbnail} alt={forum.courseTitle} className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full"><GraduationCap className="w-8 h-8 text-orange-300" /></div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {forum.isPinned && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full"><Pin className="w-2.5 h-2.5" />Pinned</span>}
                        {forum.isHot && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full"><Flame className="w-2.5 h-2.5" />Trending</span>}
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded-full">{forum.category || "General"}</span>
                      </div>
                      <h3 className="mb-1 text-base font-bold text-gray-900 transition-colors group-hover:text-orange-600">{forum.title}</h3>
                      <p className="mb-2 text-sm text-gray-500 truncate">{forum.courseTitle}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{forum.topics || 0} topics</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{forum.memberCount || 0} members</span>
                        <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{forum.posts || 0} posts</span>
                        {forum.lastActivity && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{forum.lastActivity}</span>}
                      </div>
                    </div>

                    <div className="flex flex-row items-center justify-between gap-3 sm:flex-col sm:justify-center sm:text-right">
                      {forum.isJoined ? (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-green-700 bg-green-100 rounded-full"><CheckCircle className="w-3 h-3" />Joined</span>
                          <button onClick={(e) => handleLeaveForum(e, forum)} className="text-xs font-medium text-red-500 transition-colors hover:text-red-600">Leave</button>
                        </>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); openJoinModal(forum); }} className="px-4 py-2 text-xs font-medium text-white transition-all rounded-lg shadow-sm bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md">
                          Join Forum
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Join Modal */}
          {showJoinModal && selectedForum && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl animate-scale-in">
                {/* Modal header */}
                <div className="relative p-5 bg-gradient-to-r from-orange-600 to-orange-500">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Join {selectedForum.title}</h3>
                        <p className="text-xs text-orange-100">{selectedForum.courseTitle}</p>
                      </div>
                    </div>
                    <button onClick={() => setShowJoinModal(false)} className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Modal body */}
                <div className="p-5 space-y-4">
                  {/* Instructor info */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50">
                    <div className="flex items-center justify-center w-12 h-12 overflow-hidden font-bold text-white rounded-full bg-gradient-to-br from-orange-400 to-orange-600">
                      {(selectedForum.instructor?.name || "I").charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{selectedForum.instructor?.name}</p>
                      <p className="text-xs text-gray-500">Forum Instructor</p>
                    </div>
                  </div>

                  {/* Forum stats */}
                  <div className="flex items-center justify-around p-3 rounded-xl bg-orange-50">
                    <div className="text-center"><Users className="w-4 h-4 mx-auto mb-1 text-orange-500" /><p className="text-xs font-semibold text-gray-900">{selectedForum.memberCount || 0}</p><p className="text-[10px] text-gray-500">Members</p></div>
                    <div className="text-center"><MessageSquare className="w-4 h-4 mx-auto mb-1 text-orange-500" /><p className="text-xs font-semibold text-gray-900">{selectedForum.topics || 0}</p><p className="text-[10px] text-gray-500">Topics</p></div>
                    <div className="text-center"><MessageCircle className="w-4 h-4 mx-auto mb-1 text-orange-500" /><p className="text-xs font-semibold text-gray-900">{selectedForum.posts || 0}</p><p className="text-[10px] text-gray-500">Posts</p></div>
                  </div>

                  {/* Agreement */}
                  <label className="flex items-start gap-3 p-3 transition-colors cursor-pointer rounded-xl bg-orange-50 hover:bg-orange-100">
                    <input type="checkbox" checked={agreeToRules} onChange={(e) => setAgreeToRules(e.target.checked)} className="mt-0.5 w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500 focus:ring-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">I agree to the forum rules and guidelines</p>
                      <p className="text-xs text-gray-500 mt-0.5">By joining, you can ask questions and participate in discussions</p>
                    </div>
                  </label>
                </div>

                {/* Modal footer */}
                <div className="flex gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
                  <button onClick={handleJoinForum} disabled={!agreeToRules || joining} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${agreeToRules && !joining ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:shadow-lg" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
                    {joining ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {joining ? "Joining..." : "Join Forum"}
                  </button>
                  <button onClick={() => setShowJoinModal(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
          .animate-fade-in { animation: fade-in 0.2s ease-out; }
          .animate-scale-in { animation: scale-in 0.2s ease-out; }
        `}</style>
      </main>
    </>
  );
};

export default ForumsPage;